import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatService } from '../services/chatService';
import RfiContainer from '../components/rfi/RfiContainer';
import KnowledgeTextInput from '../components/knowledge/KnowledgeTextInput';
import KnowledgeFileUpload from '../components/knowledge/KnowledgeFileUpload';
import KnowledgeDocuments from '../components/knowledge/KnowledgeDocuments';
import Chat, { ChatRef } from '../components/chat-working/Chat';
import ChatLayout from '../components/chat-working/ChatLayout';
import ChatSidebar, { Conversation } from '../components/chat-working/ChatSidebar';
import { useSidebarState } from '../hooks/useSidebarState';

interface HomeProps {
  selectedPage: string;
  onPageSelect: (page: string) => void;
}

const Home: React.FC<HomeProps> = ({ selectedPage, onPageSelect }) => {
  const navigate = useNavigate();
  const chatRef = useRef<ChatRef>(null);
  const { isCollapsed, toggleSidebar } = useSidebarState();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [showTextModal, setShowTextModal] = useState(false);
  const [citationTextContent, setCitationTextContent] = useState<string>('');
  const [citationTitle, setCitationTitle] = useState<string>('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(undefined);

  // Set default page to Query engine on mount
  useEffect(() => {
    if (!selectedPage) {
      onPageSelect('Query engine');
    }
  }, []);

  // Load conversations for Query engine
  useEffect(() => {
    if (selectedPage === 'Query engine') {
      loadConversations();
    }
  }, [selectedPage]);

  const loadConversations = async () => {
    try {
      const response = await chatService.getChatHistory(1, 100);
      const data = response.data;
      const threads = data.items || [];

      const convs: Conversation[] = threads.map((thread: any) => ({
        id: thread.thread_id,
        title: thread.title || 'Untitled Chat',
        lastMessage: thread.title,
        timestamp: thread.created_at ? new Date(thread.created_at) : undefined
      }));

      setConversations(convs);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConversations([]);
    }
  };

  const handleSelectConversation = async (convId: string) => {
    try {
      const response = await chatService.getThreadMessages(convId);
      const data = response.data;
      if (data.conversations && chatRef.current) {
        chatRef.current.handleThreadSelect(convId, data.conversations);
        setCurrentConversationId(convId);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleNewChat = () => {
    // Clear chat but stay on Query engine page
    if (chatRef.current) {
      chatRef.current.handleThreadSelect('', []);
      setCurrentConversationId(undefined);
    }
    // Reload conversations to show the previous thread in history
    loadConversations();
  };


  const handleCitationClick = async (citationId: string, page: number) => {
    try {
      // Fetch citation as blob first
      const blob = await chatService.getCitationPdf(citationId);
      console.log('Citation blob type:', blob.type, 'size:', blob.size);

      // Check if response is JSON (text citation) or PDF
      if (blob.type === 'application/json' || blob.size < 1000) {
        // Try to parse as JSON (text citation)
        try {
          const text = await blob.text();
          const data = JSON.parse(text);
          console.log('Citation Text:', data.text_content);
          setCitationTextContent(data.text_content || 'No text content available');
          setCitationTitle(data.fund || 'Citation Text');
          setShowTextModal(true);
        } catch (parseError) {
          // If parse fails, treat as PDF
          console.log('Parse failed, treating as PDF');
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setPdfPage(page);
          setShowPdfModal(true);
        }
      } else {
        // It's a PDF (large blob)
        console.log('Citation PDF, size:', blob.size);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPdfPage(page);
        setShowPdfModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch citation:', error);
    }
  };

  const truncateText = (text: string, maxLines: number = 3) => {
    const lines = text.split('\n');
    if (lines.length <= maxLines) return text;
    
    const truncated = lines.slice(0, maxLines).join('\n');
    return truncated + '...';
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      // Any cleanup needed
    };
  }, [selectedPage]);

  const renderLeftContent = () => {
    if (selectedPage === 'Query engine') {
      return null; // EmbeddedChat will handle its own layout
    }

    if (selectedPage === 'RFI File Upload') {
      return null; // RfiContainer will handle its own layout
    }

    return null;
  };

  const renderRightContent = () => {
    if (selectedPage === 'Query engine') {
      return (
        <ChatLayout
          sidebarCollapsed={isCollapsed}
          sidebar={
            <ChatSidebar
              conversations={conversations}
              currentConversationId={currentConversationId}
              collapsed={isCollapsed}
              onToggleCollapse={toggleSidebar}
              onNewChat={handleNewChat}
              onSelectConversation={handleSelectConversation}
              onRenameConversation={(id, title) => {
                setConversations(prev =>
                  prev.map(c => c.id === id ? { ...c, title } : c)
                );
              }}
              onDeleteConversation={(id) => {
                setConversations(prev => prev.filter(c => c.id !== id));
              }}
            />
          }
        >
          <Chat
            ref={chatRef}
            onCitationClick={handleCitationClick}
            onToggleSidebar={toggleSidebar}
            conversationTitle="New Chat"
          />
        </ChatLayout>
      );
    }
    if (selectedPage === 'RFI File Upload') {
      return (
        <RfiContainer onCitationClick={handleCitationClick} />
      );
    }
    if (selectedPage === 'Add knowledge') {
      return (
        <div className="card-elevated">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Add Knowledge</h1>

          <div className="space-y-6">
            <KnowledgeTextInput />

            <hr className="border-gray-200" />

            <KnowledgeFileUpload />
          </div>
        </div>
      );
    }
    if (selectedPage === 'Knowledge Library') {
      return (
        <KnowledgeDocuments />
      );
    }
    return (
      <div className="card-elevated text-center">
        <h2 className="text-4xl font-bold text-gray-800 mb-4">Evolution Credit Partners RFI Engine</h2>
        <p className="text-lg text-gray-600">Select a page to get started</p>
      </div>
    );
  };

  // Special handling for Query engine to break out of container
  if (selectedPage === 'Query engine') {
    return (
      <>
        <div className="fixed inset-0 top-[4rem] bottom-0 bg-white">
          {renderRightContent()}
        </div>

        {/* PDF Modal */}
        {showPdfModal && pdfUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Citation PDF</h3>
                <button
                  onClick={() => {
                    setShowPdfModal(false);
                    URL.revokeObjectURL(pdfUrl);
                    setPdfUrl(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <iframe
                src={`${pdfUrl}#page=${pdfPage}`}
                className="flex-1 w-full border rounded"
                title="Citation PDF"
              />
            </div>
          </div>
        )}

        {/* Text Content Modal */}
        {showTextModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl h-5/6 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{citationTitle}</h3>
                <button
                  onClick={() => {
                    setShowTextModal(false);
                    setCitationTextContent('');
                    setCitationTitle('');
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4 rounded border">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                  {citationTextContent}
                </pre>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen space-y-12">
      {!selectedPage && (
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">DDQ</h1>
          <p className="text-lg text-gray-600">Choose a page to get started</p>
        </div>
      )}

      {selectedPage && (
        selectedPage === 'Add knowledge' || selectedPage === 'RFI File Upload' || selectedPage === 'Knowledge Library' ? (
          <div className="w-full">
            {renderRightContent()}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              {renderLeftContent()}
            </div>
            <div className="lg:col-span-2">
              {renderRightContent()}
            </div>
          </div>
        )
      )}

      {/* PDF Modal */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Citation PDF</h3>
              <button 
                onClick={() => {
                  setShowPdfModal(false);
                  URL.revokeObjectURL(pdfUrl);
                  setPdfUrl(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <iframe 
              src={`${pdfUrl}#page=${pdfPage}`}
              className="flex-1 w-full border rounded"
              title="Citation PDF"
            />
          </div>
        </div>
      )}

      {/* Text Content Modal */}
      {showTextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{citationTitle}</h3>
              <button 
                onClick={() => {
                  setShowTextModal(false);
                  setCitationTextContent('');
                  setCitationTitle('');
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50 p-4 rounded border">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                {citationTextContent}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 